import React, { useState, useRef, useEffect, useCallback } from 'react';
import { MessageCircle, X, Send, Bot, User, Sparkles, Trash2 } from 'lucide-react';
import './CashperAssistant.css';

const API_BASE_URL = import.meta.env.VITE_API_URL;

const WELCOME_MESSAGE = {
  id: 'welcome',
  role: 'bot',
  content: `Hi there! 👋 I'm the Cashper Assistant.\n\nI can help you with information about our loans, insurance, investments, tax planning, and more.\n\nHow can I help you today?`,
  time: new Date(),
};

const QUICK_SUGGESTIONS = [
  'What loans do you offer?',
  'Tell me about SIP',
  'How to apply for insurance?',
  'What documents are needed?',
  'Show my applications',
];

const CashperAssistant = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const messagesContainerRef = useRef(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300);
    }
  }, [isOpen]);

  // Format time
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Get auth token from localStorage
  const getAuthToken = () => {
    return localStorage.getItem('access_token') || null;
  };

  // Build chat history for API (exclude welcome message)
  const buildHistory = () => {
    return messages
      .filter((m) => m.id !== 'welcome')
      .map((m) => ({
        role: m.role === 'bot' ? 'assistant' : 'user',
        content: m.content,
      }));
  };

  // Send message to backend
  const sendMessage = async (messageText) => {
    const text = messageText?.trim() || input.trim();
    if (!text || isLoading) return;

    // Add user message
    const userMsg = {
      id: Date.now().toString(),
      role: 'user',
      content: text,
      time: new Date(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    setShowSuggestions(false);

    try {
      const token = getAuthToken();
      const headers = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/api/chat/ask`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          message: text,
          history: buildHistory(),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const botMsg = {
          id: (Date.now() + 1).toString(),
          role: 'bot',
          content: data.reply,
          time: new Date(),
        };
        setMessages((prev) => [...prev, botMsg]);

        // If chat is closed, increment unread
        if (!isOpen) {
          setUnreadCount((prev) => prev + 1);
        }
      } else {
        const botMsg = {
          id: (Date.now() + 1).toString(),
          role: 'bot',
          content:
            "I'm having trouble connecting right now. Please try again in a moment, or contact us at info@cashper.ai 🙏",
          time: new Date(),
        };
        setMessages((prev) => [...prev, botMsg]);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const botMsg = {
        id: (Date.now() + 1).toString(),
        role: 'bot',
        content:
          "Sorry, I couldn't connect to the server. Please check your internet connection and try again. 🙏",
        time: new Date(),
      };
      setMessages((prev) => [...prev, botMsg]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle input key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle suggestion click
  const handleSuggestion = (text) => {
    sendMessage(text);
  };

  // Toggle chat window
  const toggleChat = () => {
    setIsOpen((prev) => !prev);
    if (!isOpen) {
      setUnreadCount(0);
    }
  };

  // Clear chat history
  const clearChat = () => {
    setMessages([WELCOME_MESSAGE]);
    setShowSuggestions(true);
  };

  return (
    <>
      {/* Chat Window */}
      {isOpen && (
        <div className="cashper-chat-window">
          {/* Header */}
          <div className="cashper-chat-header">
            <div className="cashper-chat-header-info">
              <div className="cashper-chat-avatar">
                <Sparkles />
              </div>
              <div className="cashper-chat-title">
                <h3>Cashper Assistant</h3>
                <span>
                  <span className="cashper-online-dot"></span>
                  Always online
                </span>
              </div>
            </div>
            <div className="cashper-chat-header-actions">
              <button
                className="cashper-chat-action-btn"
                onClick={clearChat}
                title="Clear chat"
                aria-label="Clear chat"
              >
                <Trash2 size={16} />
              </button>
              <button
                className="cashper-chat-action-btn"
                onClick={toggleChat}
                title="Close chat"
                aria-label="Close chat"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="cashper-chat-messages" ref={messagesContainerRef}>
            {messages.map((msg) => (
              <div key={msg.id} className="cashper-msg-wrapper">
                <div className={`cashper-message ${msg.role === 'user' ? 'user' : 'bot'}`}>
                  <div className="cashper-msg-avatar">
                    {msg.role === 'bot' ? <Bot /> : <User />}
                  </div>
                  <div>
                    <div className="cashper-msg-bubble">{msg.content}</div>
                    <div className="cashper-msg-time">{formatTime(msg.time)}</div>
                  </div>
                </div>
              </div>
            ))}

            {/* Typing Indicator */}
            {isLoading && (
              <div className="cashper-typing">
                <div className="cashper-typing-avatar">
                  <Bot />
                </div>
                <div className="cashper-typing-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick Suggestions */}
          {showSuggestions && messages.length <= 1 && (
            <div className="cashper-suggestions">
              {QUICK_SUGGESTIONS.map((suggestion, idx) => (
                <button
                  key={idx}
                  className="cashper-suggestion-chip"
                  onClick={() => handleSuggestion(suggestion)}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          )}

          {/* Input Area */}
          <div className="cashper-chat-input-area">
            <textarea
              ref={inputRef}
              className="cashper-chat-input"
              placeholder="Type your message..."
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyPress}
              rows={1}
              disabled={isLoading}
            />
            <button
              className={`cashper-chat-send ${input.trim() ? 'active' : ''}`}
              onClick={() => sendMessage()}
              disabled={!input.trim() || isLoading}
              aria-label="Send message"
            >
              <Send />
            </button>
          </div>

          {/* Footer */}
          <div className="cashper-chat-footer">
            Powered by Cashper AI ✨
          </div>
        </div>
      )}

      {/* Floating Toggle Button */}
      <button
        className={`cashper-chat-toggle ${isOpen ? 'open' : ''}`}
        onClick={toggleChat}
        aria-label={isOpen ? 'Close chat' : 'Open Cashper Assistant'}
      >
        {isOpen ? <X /> : <MessageCircle />}
        {unreadCount > 0 && !isOpen && (
          <span className="cashper-unread-badge">{unreadCount}</span>
        )}
      </button>
    </>
  );
};

export default CashperAssistant;
